import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DeviceResponseType as ChannelResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import {
  ConfigurationStateEnum,
  FacadePositionDeviceState,
  FacadeType,
  FacadeTypeDeviceState,
  WindThresholdStateEnum,
} from 'lavva.exalushome/build/js/Services/Devices/IDeviceState';
import {
  BlindRemoteButtonState,
  IDeviceState as IChannelState,
} from 'lavva.exalushome/build/js/Services/Devices/IDeviceState';
import { IconConnectionError, IconWarning } from '../../../../../../../assets/icons';
import { ControlWrapper, EmptyStateBox, Slider } from '../../../../../../../components';
import { BlindDetailsSimpleControlButtons } from '../../../../../../../components/channel-details-controls/blind-simple';
import { BlindDetailsControls } from '../../../../../../../components/channel-details-controls/blind/controls';
import LavvaDetailsWrapper from '../../../../../../../components/details-wrapper';
import { useSomethingWrong } from '../../../../../modules/channel-list/hooks/use-something-wrong';
import { useFacadeControl } from '../../../hooks/use-facade-control';
import { useWindControl } from '../../../hooks/use-wind-control';
import { SlatsSection } from './components/slats-section';
import { Favourites } from './favourites';
import './index.scss';

type FacadeDetailsControlsProps = {
  channel: IDeviceChannel;
  active: boolean;
};

export const FacadeDetailsControls: React.FC<FacadeDetailsControlsProps> = ({ channel, active }) => {
  const { t: tc } = useTranslation('common');
  const { t: td } = useTranslation('channel-details');
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [tiltValue, setTiltValue] = useState<number>(0);
  const [targetTiltValue, setTargetTiltValue] = useState(0);
  const [targetValue, setTargetValue] = useState<number>(0);
  const [activeTarget, setActiveTarget] = useState<'blind' | 'tilts' | null>(null);
  const { handleSliderEvent, stop, open, close, handleTiltEvent } = useFacadeControl(channel);
  const { windThreshold } = useWindControl(channel);
  const { connectionIcon, calibrationNeed } = useSomethingWrong(channel, {
    detailsView: true,
  });

  const state = useMemo(() => {
    return channel.States?.find(
      (state) => state.TypeAsEnum === ChannelResponseType.FacadePosition,
    ) as FacadePositionDeviceState;
  }, [channel.States]);

  const isUnknownFacadePosition = useMemo(() => {
    const facadeData = (
      channel.States?.find(
        (state) => state.TypeAsEnum === ChannelResponseType.FacadePosition,
      ) as FacadePositionDeviceState
    )?.Data;

    return facadeData?.Position === 255 || facadeData?.Tilt === 255;
  }, [channel.States]);

  const remoteButtonState = useMemo(() => {
    return channel.States?.find(
      (state) => state.TypeAsEnum === ChannelResponseType.BlindRemoteButtonState,
    ) as IChannelState<BlindRemoteButtonState>;
  }, [channel.States]);

  const facadeType = useMemo(() => {
    return channel?.States?.find(
      (state) => state.TypeAsEnum === ChannelResponseType.FacadeType,
    ) as FacadeTypeDeviceState;
  }, [channel?.States]);

  useEffect(() => {
    if (typeof state?.Data?.Position === 'number') {
      if (state?.Data?.Position === sliderValue && targetValue !== sliderValue) return;
      setSliderValue(state?.Data?.Position);
      setTargetValue(state?.Data?.Position);
    }
    if (typeof state?.Data?.Tilt === 'number' && !active) {
      if (state?.Data?.Tilt === tiltValue) return;
      setTiltValue(state?.Data?.Tilt);
      setTargetTiltValue(state?.Data?.Tilt);
    }
  }, [JSON.stringify(channel.States), active]);

  const position = state?.Data?.Position ?? null;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActiveTarget('blind');
    setTargetValue(Number(event.target.value));
  };

  const handleDown = () => {
    setActiveTarget('blind');
    close();
  };

  const handleStop = () => {
    setActiveTarget('blind');
    stop();
    setTargetValue(sliderValue);
  };

  const handleUp = () => {
    setActiveTarget('blind');
    open();
  };

  const activeBlind = activeTarget === 'blind' && active;
  const activeTilts = activeTarget === 'tilts' && active;

  return (
    <div
      className={classNames('cover-detail-view facade', {
        'cover-detail-view--full-space': calibrationNeed,
      })}
    >
      {calibrationNeed ? (
        <LavvaDetailsWrapper>
          <ControlWrapper>
            {connectionIcon && <IconConnectionError big size={14} />}
            <div>
              {calibrationNeed && (
                <EmptyStateBox
                  header={tc('status.calibrate')}
                  content={td(
                    calibrationNeed !== ConfigurationStateEnum.OngoingConfiguration
                      ? 'calibrationInProgressInfo'
                      : 'calibrationNeededSimple',
                  )}
                  icon={<IconWarning />}
                  className="m-b-24"
                />
              )}
              <BlindDetailsSimpleControlButtons
                open={open}
                stop={stop}
                close={close}
                remoteButtonState={remoteButtonState}
                active={activeBlind}
              />
            </div>
          </ControlWrapper>
        </LavvaDetailsWrapper>
      ) : (
        <>
          {windThreshold !== WindThresholdStateEnum.Exceeded && (
            <LavvaDetailsWrapper>
              <ControlWrapper className="control-wrapper--space-evenly control-wrapper--full-space">
                <div className="max-width-desktop">
                  <div className={classNames('slider-section', { disabled: activeTilts })}>
                    {connectionIcon && <IconConnectionError big size={14} />}
                    <Slider
                      onPointerUp={handleSliderEvent}
                      value={sliderValue}
                      targetValue={targetValue}
                      onChange={onChange}
                      active={activeBlind}
                      showValue={false}
                      className="slider--width-auto"
                      disabled={activeTilts}
                    />
                  </div>
                  <SlatsSection
                    tiltValue={tiltValue}
                    fullRange={facadeType?.Data.FType === FacadeType.Facade180}
                    defaultValue={100 - targetTiltValue}
                    value={100 - targetTiltValue}
                    onChange={(_, value) => {
                      setActiveTarget('tilts');
                      setTargetTiltValue(100 - (value as number));
                    }}
                    onChangeCommitted={() => {
                      handleTiltEvent(targetTiltValue);
                    }}
                    activeTilts={activeTilts}
                    activeBlind={activeBlind}
                  />
                </div>
              </ControlWrapper>
            </LavvaDetailsWrapper>
          )}
          {isUnknownFacadePosition && (
            <EmptyStateBox
              header={td('exalus.facade.unknownState.header')}
              content={td('exalus.facade.unknownState.content')}
              icon={<IconWarning />}
              className="m-t-24"
            />
          )}
          {windThreshold === WindThresholdStateEnum.Exceeded ? (
            <EmptyStateBox
              header={td('exalus.windThresholdExceeded.windExceeded')}
              content={td('exalus.windThresholdExceeded.blockDetailsInfo')}
              icon={<IconWarning />}
              className="m-t-24"
            />
          ) : (
            <>
              <BlindDetailsControls
                downButton={{
                  handleClick: handleDown,
                }}
                stopButton={{
                  handleClick: handleStop,
                }}
                upButton={{
                  handleClick: handleUp,
                }}
                disabledPadding
              />
              <Favourites channel={channel} setTargetValue={setTargetValue} position={position} />
            </>
          )}
        </>
      )}
    </div>
  );
};
